import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';
import Pagination from '../../components/common/Pagination';

function CompanyManagementPage() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // 통계용 (전체 데이터 기반)
    const [stats, setStats] = useState({
        totalCompanies: 0,
        activeCompanies: 0,
        totalUsers: 0,
        totalEquipments: 0
    });

    // 회사 목록 조회
    useEffect(() => {
        fetchCompanies();
    }, [page, size]);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies?page=${page}&size=${size}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                setCompanies(data.content || []);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);

                // 통계 계산 (현재 페이지 데이터 기반)
                const content = data.content || [];
                if (page === 0) {
                    setStats({
                        totalCompanies: data.totalElements || 0,
                        activeCompanies: content.filter(c => isCompanyActive(c)).length,
                        totalUsers: content.reduce((sum, c) => sum + (c.userCount || 0), 0),
                        totalEquipments: content.reduce((sum, c) => sum + (c.equipmentCount || 0), 0)
                    });
                }
            }
        } catch (error) {
            console.error('회사 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('ko-KR');
    };

    // 회사 상태 판별 (isActive 또는 subscriptionStatus 기반)
    const getCompanyStatus = (company) => {
        if (company.isActive === false) return 'INACTIVE';
        if (company.subscriptionStatus) return company.subscriptionStatus;
        return 'ACTIVE';
    };

    const isCompanyActive = (company) => {
        const status = getCompanyStatus(company);
        return status === 'ACTIVE' || status === 'TRIAL';
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    // 회사 삭제
    const handleDeleteCompany = async (company) => {
        const confirmMessage = `정말 "${company.companyName}" 회사를 삭제하시겠습니까?\n\n삭제 시 다음 데이터가 모두 삭제됩니다:\n- 회사 정보\n- 모든 사용자 계정\n- 테넌트 스키마 및 데이터\n\n이 작업은 되돌릴 수 없습니다.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies/${company.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();

            if (result.success) {
                alert(`"${company.companyName}" 회사가 삭제되었습니다.`);
                fetchCompanies();
            } else {
                alert(`삭제 실패: ${result.error || '알 수 없는 오류'}`);
            }
        } catch (error) {
            console.error('회사 삭제 실패:', error);
            alert('회사 삭제 중 오류가 발생했습니다.');
        }
    };

    return (
        <div className="platform-page">
            <div className="platform-page-header">
                <h1 className="platform-page-title">회사 관리</h1>
                <p className="platform-page-description">등록된 테넌트(회사)를 관리합니다.</p>
            </div>

            {/* 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">business</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{stats.totalCompanies}</div>
                        <div className="platform-stat-label">전체 회사</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {companies.filter(c => isCompanyActive(c)).length}
                        </div>
                        <div className="platform-stat-label">활성 회사</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">group</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {companies.reduce((sum, c) => sum + (c.userCount || 0), 0)}
                        </div>
                        <div className="platform-stat-label">전체 사용자</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">precision_manufacturing</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {companies.reduce((sum, c) => sum + (c.equipmentCount || 0), 0)}
                        </div>
                        <div className="platform-stat-label">전체 설비</div>
                    </div>
                </div>
            </div>

            {/* 회사 목록 테이블 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">회사 목록</h3>
                    <button className="platform-btn platform-btn-primary" onClick={() => navigate('/platform/companies/register')}>
                        <span className="material-symbols-outlined">add</span>
                        회사 등록
                    </button>
                </div>
                {loading ? (
                    <div className="platform-empty">
                        <div className="platform-empty-text">로딩 중...</div>
                    </div>
                ) : companies.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">business</span>
                        <div className="platform-empty-text">등록된 회사가 없습니다.</div>
                    </div>
                ) : (
                    <>
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>회사코드</th>
                                    <th>회사명</th>
                                    <th>스키마</th>
                                    <th>대표자</th>
                                    <th>사용자</th>
                                    <th>플랜</th>
                                    <th>상태</th>
                                    <th>등록일</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map((company) => (
                                    <tr key={company.id}>
                                        <td><strong>{company.companyCode}</strong></td>
                                        <td>{company.companyName}</td>
                                        <td><code style={{ color: '#f59e0b' }}>{company.schemaName}</code></td>
                                        <td>{company.representativeName || '-'}</td>
                                        <td>{company.userCount || 0}명</td>
                                        <td>{company.subscriptionPlan || '-'}</td>
                                        <td>
                                            <span className={`platform-badge ${isCompanyActive(company) ? 'active' : 'inactive'}`}>
                                                {getCompanyStatus(company)}
                                            </span>
                                        </td>
                                        <td>{formatDate(company.createdAt)}</td>
                                        <td>
                                            <div className="platform-actions">
                                                <button
                                                    className="platform-action-btn edit"
                                                    title="수정"
                                                    onClick={() => navigate(`/platform/companies/${company.id}`)}
                                                >
                                                    <span className="material-symbols-outlined">edit</span>
                                                </button>
                                                <button
                                                    className="platform-action-btn delete"
                                                    title="삭제"
                                                    onClick={() => handleDeleteCompany(company)}
                                                >
                                                    <span className="material-symbols-outlined">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default CompanyManagementPage;
